import styled, { css } from 'styled-components'

/**
 * Form layout primitives for FormFields-based forms.
 *
 * Deliberately NOT Semantic UI's `<Form>` (`.ui.form`): Semantic layers its
 * own error skin (pink inputs, soft borders, dark-red labels) onto
 * `.field.error`, which made admin forms render errors differently from the
 * order form (whose container is a plain styled.form — OrderForm.styled.js).
 * These primitives replicate only the layout `.ui.form` provided:
 * `.fields` rows with a 1em gutter and `.field` block spacing — values
 * mirrored from fomantic-ui-less/definitions/collections/form.less
 * (`@gutterWidth: 1em`, `@rowDistance: 1em`).
 */

/** Semantic's `@gutterWidth` — the full gap between two fields in a row. */
const GUTTER = '1em'

/**
 * Plain form element. Keeps real <form> semantics (submit on Enter, onSubmit)
 * without the `.ui.form` class.
 */
export const StyledForm = styled.form`
  display: block;
  width: 100%;

  /* Replaces '.ui.form .field { margin: 0 0 1em }' for fields placed straight
     on the form rather than inside a FieldGroup. The two-class selector
     outranks the 'padding: 0.5em 0' each field root declares on itself
     (FormInput/FormSelect/FormCheckbox/FormTextArea .styled.js), so the
     spacing does not depend on stylesheet injection order. */
  > .field {
    margin: 0 0 1em;
  }

  > .field:last-child {
    margin-bottom: 0;
  }
`

/**
 * Replaces `Form.Group widths='equal'` — a flex row of equal-width fields.
 * Pass $columns={3} to replace `Form.Group widths='3'` (fixed thirds).
 *
 * The gutter is `gap`, not child padding: every field root is a
 * `styled(Form.Field)` carrying its own `padding: 0.5em 0` shorthand at the
 * same (single-class) specificity a `> *` rule here would have, and it is
 * injected later — so a padding-based gutter resolved to 0 and the fields
 * rendered flush against each other. `gap` belongs to the container and
 * cannot be overridden by a child at all.
 */
export const FieldGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${GUTTER};
  margin: 0 0 1em;

  > * {
    ${({ $columns }) =>
      $columns
        ? css`
            /* Bases sum to 100% and the gaps push the row over, so the shared
               flex-shrink pulls each column back by an equal slice. */
            flex: 0 1 calc(100% / ${$columns});
          `
        : css`
            /* 0% rather than a bare 0: jsdom's shorthand parser drops the
               whole declaration on a unitless basis, so tests would measure a
               layout the browser never renders. */
            flex: 1 1 0%;
          `}
    min-width: 0;
    margin-bottom: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

/**
 * Replaces bare `Form.Field` block wrappers. Direct raw labels keep the
 * weight/spacing `.ui.form .field > label` used to give them.
 * Pass $required to mark the label with the same red asterisk
 * StyledFormField renders (FormInput.styled.js).
 */
export const Field = styled.div`
  margin: 0 0 1em;

  > label {
    display: block;
    font-weight: 700;
    font-size: 0.92857143em;
    margin: 0 0 0.28571429rem;
  }

  ${({ $required }) =>
    $required &&
    css`
      > label {
        position: relative;
        padding-left: 0.75em;

        &::before {
          color: var(--red);
          content: '* ';
          font-size: 1.4em;
          left: 0;
          position: absolute;
          top: 0.2em;
        }
      }
    `}

  &:last-child {
    margin-bottom: 0;
  }
`
